import { createState } from "./state-subject";
import { preventAnimation, accessibilityEnabledListener } from "../services/accessibility.service";
import { INLINE_CART_SHOW } from "../constants/events.constants";
/**
 * method that determines if the current page matches any of the passed in template names
 */
export function isOnPage(...templateNames) {
    const currentTemplate = document.getElementsByTagName('html')[0].classList[1];
    let match = false;
    if (currentTemplate) {
        match = !!templateNames.find((t) => currentTemplate.indexOf(t) > -1);
    }
    return match;
}
/**
 * Waits for one or more elements to appear in the DOM. Useful if the elements are injected sometime after the DOM is loaded.
 * If the Promise is resolved, then at least one element was found for the selector. Otherwise, no elements were found after max attempts.
 */
export function waitForElements(selector, context = document) {
    const max = 10;
    let attempt = 0;
    const poll = (resolve, reject) => {
        attempt++;
        if (attempt === max) {
            console.log(`Reached max attempts waiting for selector: ${selector}.`);
            reject();
        }
        else {
            const nodeList = context.querySelectorAll(selector);
            if (nodeList.length) {
                resolve(nodeList);
            }
            else {
                setTimeout(() => {
                    poll(resolve, reject);
                }, 500);
            }
        }
    };
    return new Promise((resolve, reject) => {
        poll(resolve, reject);
    });
}
/**
 * ensures there is a "peek" of a child element when the list of child elements goes beyond the container
 * @param {HTMLElement} container
 */
export function showElementPeek(container) {
    if (container) {
        if (container.scrollWidth > container.offsetWidth) {
            const windowWidth = window.innerWidth;
            // ratio that will allow a "peek" of the next filter
            const ratio = windowWidth < 500 ? (1 / 1.5) : windowWidth < 800 ? (1 / 2.5) : (1 / 3.5);
            for (const el of container.children) {
                const newWidth = container.offsetWidth * ratio;
                const aspectRatio = window.getComputedStyle(el).aspectRatio;
                if (aspectRatio) {
                    const arArray = aspectRatio.split("/").map(r => +(r.trim()));
                    // new width * ar height / ar width
                    const newHeight = newWidth * arArray[1] / +arArray[0];
                    el.style.minWidth = 'auto';
                    el.style.width = `${newWidth}px`;
                    el.style.height = `${newHeight}px`;
                }
            }
        }
    }
}
/**
 * generates a srcset based on the url and list of widths
 * widths are also doubled to handle x2 res devices
 */
export function createSrcset(url, widths) {
    let srcset = '';
    if (url && widths) {
        const delimiter = url.includes("?") ? "&" : "?";
        const allWidths = widths.concat(widths.map(w => w * 2));
        const uniqueWidths = [...new Set(allWidths)];
        srcset = uniqueWidths.sort(function (a, b) { return a - b; }).map(w => `${url}${delimiter}width=${w} ${w}w`).join(", ");
    }
    return srcset;
}
const ICONS_SELECTORS = ['#usntA40Toggle', '#kustomer-ui-sdk-iframe'];
let floatingIconsOffset = 0;
function applyFloatingIconOffset(icon) {
    const storedBase = parseInt(icon.dataset.floatingIconBaseBottom || '', 10);
    const baseBottom = Number.isNaN(storedBase)
        ? parseInt(window.getComputedStyle(icon).bottom || '0', 10)
        : storedBase;
    if (!icon.dataset.floatingIconBaseBottom) {
        icon.dataset.floatingIconBaseBottom = String(baseBottom);
    }
    icon.style.transition = 'bottom 0.5s';
    icon.style.bottom = `${baseBottom + floatingIconsOffset}px`;
}
function applyFloatingIconsOffset() {
    ICONS_SELECTORS.forEach((selector) => {
        // we need to wait for elements separately as they appear asynchronously on page load
        waitForElements(selector).then((icons) => {
            applyFloatingIconOffset(icons[0]);
        }).catch(() => { });
    });
}
/**
 * Moves the floating icons at the bottom of the page up/down to avoid covering other elements.
 * Positive numbers will move the icon higher; negative, lower.
 */
export function updateFloatingIconsPosition(change) {
    floatingIconsOffset += change;
    applyFloatingIconsOffset();
}
/** Re-applies the current offset — call when a floating icon loads after the page (e.g. Kustomer). */
export function syncFloatingIconsPosition() {
    applyFloatingIconsOffset();
}
/**
 * fetch with a timeout (see: https://dmitripavlutin.com/timeout-fetch-request/)
 */
export async function fetchWithTimeout(url, options = {}) {
    const { timeout = 2000 } = options;
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    const response = await fetch(url, {
        ...options,
        signal: controller.signal
    });
    clearTimeout(id);
    return response;
}
/**
 * gets date in future based on number of days
 *
 */
export function getFutureDate(days) {
    const date = new Date();
    if (!days) {
        console.error("Unable to get future date; no days defined.");
    }
    else {
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    }
    return date;
}
// scroll height at which we want to transition from the full header to a smaller header
const HEADER_TRANSITION_HEIGHT = 180;
const SCROLL_LISTENER_OPTS = { passive: true };
const headerTransitionStore = createState(false);
function setHeaderTransitionSmaller(smaller) {
    if (headerTransitionStore.get() === smaller) {
        return;
    }
    headerTransitionStore.set(smaller);
}
/** Emits the current header transition state and notifies subscribers on change. */
export const headerTransition = {
    subscribe(listener) {
        return headerTransitionStore.subscribe(listener);
    },
};
let lastScrollY = 0;
const scrollBufferPx = 20;
const scrollEvent = () => {
    // provide buffer to make transition feel less "touchy"
    if (Math.abs(lastScrollY - window.scrollY) < scrollBufferPx) {
        return;
    }
    const isScrollingDown = window.scrollY > lastScrollY;
    const isBelowTransitionHeight = window.scrollY > HEADER_TRANSITION_HEIGHT;
    if (isScrollingDown && isBelowTransitionHeight) {
        setHeaderTransitionSmaller(true);
    }
    else {
        setHeaderTransitionSmaller(false);
    }
    lastScrollY = window.scrollY;
};
function stopHeaderScrollTransition() {
    console.log("stopping header transition");
    window.removeEventListener("scroll", scrollEvent, SCROLL_LISTENER_OPTS);
}
window.addEventListener("scroll", scrollEvent, SCROLL_LISTENER_OPTS);
// Same as former merge(of(associate), of(preventAnimation), accessibility) + filter: any truthy disables scroll-driven header transition
if (document.body.classList.contains("customers-account-associate") ||
    preventAnimation()) {
    stopHeaderScrollTransition();
}
accessibilityEnabledListener().subscribe(() => {
    stopHeaderScrollTransition();
});
headerTransitionStore.subscribe((smallerHeader) => {
    // setTimeout 0 used for smoother animation
    setTimeout(() => {
        if (!smallerHeader) {
            document.body.style.paddingTop = "0px";
        }
    }, 0);
    if (smallerHeader) {
        document.body.classList.add("utk-smaller-header");
        document.body.classList.remove("utk-full-header");
    }
    else {
        document.body.classList.add("utk-full-header");
        document.body.classList.remove("utk-smaller-header");
    }
});
/**
 * updates the padding-top property on the body element.
 * used to prevent jerkiness when showing/hiding header elements
 */
export function updateBodyPaddingTop(num) {
    const currentPadding = parseInt(document.body.style.paddingTop) || 0;
    document.body.style.paddingTop = `${currentPadding + num}px`;
}
/**
 * rechecks the expression every interval until the value is either true or we reach the max retries
 * useful when waiting for a global variable to be defined (i.e. check = () => window.someGlobalProperty)
 *
 */
export function waitForTruthyVal(check, interval = 100, maxRetries = 10) {
    let attempt = 0;
    const poll = (resolve, reject) => {
        attempt++;
        if (attempt === maxRetries) {
            reject(`Maximum number of retries (${maxRetries}) reached. Did not find a truthy value.`);
        }
        else {
            const val = check();
            if (val) {
                resolve(val);
            }
            else {
                setTimeout(() => {
                    poll(resolve, reject);
                }, interval);
            }
        }
    };
    return new Promise((resolve, reject) => {
        poll(resolve, reject);
    });
}
/**
* returns the element or parent based on the provided classname. returns null if no such element exists.
* useful when a target element in an event listener has nested elements
*
*/
export function getElementOrParentByClassName(el, className) {
    return el.classList.contains(className) ? el : el.closest(`.${className}`);
}
export function removeFalsy(obj) {
    return Object.entries(obj)
        .filter(e => {
        const val = e[1];
        return val !== undefined && val !== null && val !== '';
    })
        .reduce((acc, curr) => {
        const [key, val] = curr;
        acc[key] = val;
        return acc;
    }, {});
}
export function showInlineCart() {
    window.dispatchEvent(new CustomEvent(INLINE_CART_SHOW));
}
export function chunkArr(arr, size) {
    return arr.reduce((acc, _, i) => {
        if (i % size === 0)
            acc.push(arr.slice(i, i + size));
        return acc;
    }, []);
}
export function getCurrentPosition() {
    return new Promise((resolve, reject) => {
        if (window.navigator.geolocation) {
            window.navigator.geolocation.getCurrentPosition(resolve, reject);
        }
        else {
            reject(new Error("Geolocation is not supported by this browser."));
        }
    });
}
export function handleize(str) {
    if (!str)
        return "";
    return str
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
}
/**
 * adds https:// to the url if it doesn't already have it
 * @param url
 * @returns
 */
export function addUrlProtocol(url) {
    if (!url)
        return "";
    if (url.startsWith("http")) {
        return url;
    }
    return `https://${url}`;
}
/**
 * Debounce function that delays execution of a function until after a specified wait time
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns A debounced version of the function
 */
export function debounce(func, wait) {
    let timeout = null;
    return function executedFunction(...args) {
        const later = () => {
            timeout = null;
            func(...args);
        };
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(later, wait);
    };
}
export function withoutTrailingZeros(price) {
    if (typeof price === 'number') {
        price = price.toFixed(2);
    }
    return price ? `${price}`.replace(".00", "") : '';
}
